import { useMutation } from '@apollo/react-hooks';
import { useAccount, useLogin } from '@bnnvara/accounts';
import { TrackEvent } from '@bnnvara/analytics';
import { sizes } from '@bnnvara/breakpoints';
import { BaseButton, FollowBrandButton, IconButton } from '@bnnvara/buttons';
import { BaseCard } from '@bnnvara/cards';
import { Close as CloseIcon } from '@bnnvara/icons/src/components/UserInterfaceIcons/Close';
import { Plus } from '@bnnvara/icons/src/components/UserInterfaceIcons/Plus';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';

import { followRequest } from '../../../api/mutations/brandFollow.gql';
import resizeImageSource from '../../../helpers/resizeImageSource';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import ProfileContext from '../../Profile/context';
import { FollowButtonContainer } from './styled';

// When the isEditing state is true, a user is able to remove a followed brand.
const BrandCard = ({
  brandId,
  name,
  backgroundImageUrl,
  follow,
  editFollowedBrands,
}) => {
  const image =
    backgroundImageUrl || '/static/fallbackImages/videofallback.jpg';

  const isDesktop = useIsDesktop(sizes.md);
  const { isLoggedIn, login } = useLogin();
  const { token } = useAccount();
  const [isFollowedByUser, setIsFollowedByUser] = useState(false);
  const { setUnfollowedBrands } = useContext(ProfileContext);

  const CloseIconButton = () => (
    <IconButton>
      <CloseIcon />
    </IconButton>
  );

  const handleClick = async (followMutation) => {
    // TODO: add FollowedBrandsQuery to check if user follows any title(s) instead of current title (will do that icw with onBoarding going live)
    setIsFollowedByUser(!isFollowedByUser);

    setUnfollowedBrands(brandId);
    if (!isFollowedByUser) {
      TrackEvent('profiel', 'click', 'volgen');
    } else {
      TrackEvent('profiel', 'click', 'ontvolgen');
    }

    return followMutation({
      variables: {
        interactionId: brandId,
        token,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        followRequest: true,
      },
    });
  };

  const [followContent] = useMutation(followRequest, {
    onError: () => setIsFollowedByUser(!isFollowedByUser),
  });

  const FollowButton = () => {
    return (
      <FollowButtonContainer>
        {!isLoggedIn ? (
          <BaseButton
            size="small"
            type="button"
            variant="outline"
            onClick={() => {
              TrackEvent('profiel_volgknop', 'click', 'inloggen');
              login();
            }}
          >
            <Plus color="currentColor" />
            Volgen
          </BaseButton>
        ) : (
          <FollowBrandButton
            color="black"
            onClick={() => handleClick(followContent)}
            isFollowedByUser={isFollowedByUser}
          />
        )}
      </FollowButtonContainer>
    );
  };

  return (
    <BaseCard
      isLazyLoaded
      smWithBorder={editFollowedBrands}
      darkenImage={editFollowedBrands}
      title={name}
      backgroundImage={resizeImageSource(image, 500, 1 / 1)}
      follow={follow && isDesktop && <FollowButton />}
      followMobile={follow && !isDesktop && <FollowButton />}
      icon={editFollowedBrands && <CloseIconButton />}
      followAction={() => handleClick(followContent)}
    />
  );
};

BrandCard.defaultProps = {
  backgroundImageUrl: null,
  follow: false,
  editFollowedBrands: false,
};

BrandCard.propTypes = {
  brandId: PropTypes.string,
  editFollowedBrands: PropTypes.bool,
  name: PropTypes.string.isRequired,
  backgroundImageUrl: PropTypes.string,
  follow: PropTypes.bool,
};

export default BrandCard;
